import React, { useState, useEffect, useCallback, useRef } from 'react';
import { get, toNumber } from 'lodash';
import { useMutation, useLazyQuery } from '@apollo/client';
import PropTypes from 'prop-types';
import IconDiscount from 'components/icons/iconDiscount';
import CloseIcon from 'components/icons/iconClose';
import { useClickAway } from 'react-use';
import getZipcode from 'commons/getZipcode';
import ProtectionPlanButtonAndModal from 'components/consumer/ProtectionPlanButtonAndModal';
import Currency from 'components/consumer/Currency';
import { GET_CART, GET_CHECKOUT_SUMMARY } from 'mocks/queries';
import { UPDATE_CART_ITEM_PROTECTION_PLAN } from 'mocks/mutations';
import { getCheckoutRef } from 'components/consumer/checkout/utils/checkout';
import { logError } from 'commons/logger';
import parseCartItemErrors from 'commons/parseCartItemErrors';
import { colorGray } from 'styles/layout/colors';
import IconInfoNew from 'components/icons/IconInfoNew';
import QuantitySelect from './quantity-select';
import ProductImage from './components/ProductImage';
import ProductDescription from './components/ProductDescription';
import StatusLabel from '../StatusLabel';

const CartProduct = ({
  cartToken,
  closeCart,
  openProtectionModal,
  hasImageLink,
  product,
  removeExtraCushion,
  removeItem,
  removeItemLoading,
  viewType = '',
}) => {
  const { issue } = parseCartItemErrors(product && product.cart_item_errors);
  const [isRemovePPLoading, setIsRemovePPLoading] = useState(false);
  const [isRemovePPError, setIsRemovePPError] = useState(null);
  const [isDisplayClearanceTooltip, setDisplayClearanceTooltip] = useState(
    false
  );
  const tooltipRef = useRef(null);
  const clearanceBtnRef = useRef(null);
  const [getCart] = useLazyQuery(GET_CART, { fetchPolicy: 'network-only' });
  const [getCheckoutSummary] = useLazyQuery(GET_CHECKOUT_SUMMARY, {
    fetchPolicy: 'network-only',
  });
  const [updateCartItemProtectionPlanMutation] = useMutation(
    UPDATE_CART_ITEM_PROTECTION_PLAN
  );

  const closeTooltip = useCallback(() => {
    setDisplayClearanceTooltip(prevDisplayClearanceTooltip => {
      if (prevDisplayClearanceTooltip) return false;
      return prevDisplayClearanceTooltip;
    });
  }, []);

  useClickAway(tooltipRef, closeTooltip);
  const removeProtectionPlanOnCartItem = useCallback(async () => {
    const cartItemId = product.id;
    try {
      let success = null;
      try {
        const response = await updateCartItemProtectionPlanMutation({
          variables: {
            cart_id: toNumber(cartItemId),
            plan_id: null,
            protection_plan: false,
          },
        });

        success = get(response, 'data.updateCartItemProtectionPlan', null);
      } catch (e) {
        setIsRemovePPError('Error occurred. Please try again.');
      }
      if (success) {
        const zipCode = getZipcode();
        const checkoutRef = getCheckoutRef();
        await Promise.all([
          getCart({
            context: { noBatch: true },
            variables: {
              zipCode,
              ...(checkoutRef ? { checkoutRef } : {}),
            },
          }),
          getCheckoutSummary({
            context: { noBatch: true },
            variables: {
              ...(checkoutRef ? { checkoutRef } : {}),
              input: {
                zipcode: zipCode,
              },
            },
          }),
        ]);
      }
      setIsRemovePPLoading(false);
    } catch (e) {
      setIsRemovePPLoading(false);
    }
  }, [
    getCart,
    getCheckoutSummary,
    product.id,
    updateCartItemProtectionPlanMutation,
  ]);

  useEffect(() => {
    if (isRemovePPLoading) {
      removeProtectionPlanOnCartItem().catch(logError);
      setIsRemovePPError(null);
    }
  }, [isRemovePPLoading, removeProtectionPlanOnCartItem]);

  return (
    <div
      className="border-b border-solid border-gray-light7 py-8 px-0 md:py-5 m-0 md:m-[unset] last:border-b-0"
      data-xc="cart-product"
    >
      <div className="p-0">
        <div className="flex flex-wrap flex-row items-stretch relative last:border-b-0">
          <div className="basis-[33.33333%] min-w-0">
            <ProductImage
              closeCart={closeCart}
              hasImageLink={hasImageLink}
              product={product}
              height={80}
              key={product?.id}
            />
          </div>

          <div className="basis-[41.66667%] min-w-0">
            <h2 className="[&_span]:text-gray m-0 text-base font-bold text-gray leading-none">
              {product.name}
            </h2>

            <div className="[&_span]:text-gray text-gray-light3 text-xs flex flex-col my-2 mx-0">
              <ProductDescription
                product={product}
                removeExtraCushion={removeExtraCushion}
              />
            </div>

            <div className="w-16 text-base text-gray">
              <QuantitySelect
                issue={issue}
                product={product}
                removeProduct={removeItem}
                removeProductLoading={removeItemLoading}
                cartToken={cartToken}
                viewType={viewType}
              />
            </div>
            <div className="material-section flex pr-8 last:pr-0" />
          </div>

          <div className="basis-1/4 min-w-0">
            <div className="text-right text-base flex flex-col justify-between h-full">
              <div className="cart-price-and-clearance">
                <div className="flex items-center justify-end text-sm font-bold">
                  {product.discount_amount > 0 ? (
                    <>
                      <div className="text-sm font-bold text-gray line-through my-0 mx-[0.375rem]">
                        <Currency className="text-gray">
                          {product.price_pre_discount}
                        </Currency>
                      </div>

                      <div>
                        <Currency className="text-red">
                          {product.price}
                        </Currency>
                      </div>
                    </>
                  ) : (
                    <div className="text-sm font-bold leading-none">
                      <Currency className="text-gray">{product.price}</Currency>
                    </div>
                  )}
                </div>
                {product.is_clearance && !product.flash_sale && (
                  <button
                    onClick={e => {
                      e.stopPropagation();
                      e.preventDefault();
                      setDisplayClearanceTooltip(
                        prevIsDisplayClearanceTooltip =>
                          !prevIsDisplayClearanceTooltip
                      );
                    }}
                    ref={clearanceBtnRef}
                    type="button"
                  >
                    <StatusLabel className="w-fit cursor-pointer flex items-center whitespace-nowrap">
                      Final Sale
                      <IconInfoNew
                        height={13}
                        width={13}
                        className="ml-1.5 bottom-px md:bottom-0"
                      />
                    </StatusLabel>
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
        {isDisplayClearanceTooltip && (
          <div
            className="relative flex flex-row p-4 my-2.5 bg-brand-lightest"
            id={`clearance-${product.id}`}
            ref={tooltipRef}
          >
            <button
              onClick={event => {
                event.stopPropagation();
                closeTooltip();
              }}
              type="button"
              className="absolute right-2.5 top-2.5 w-7 h-7 flex justify-center items-center"
            >
              <CloseIcon color={colorGray} width="12" height="12" />
            </button>
            <IconDiscount width="100" className="mr-2.5" />
            <div className="text-sm">
              Final Sale
              <div className="text-xs">
                This final sale product is eligible for cancellation within 24
                hours of order placement. It is ineligible for returns except
                for items arriving damaged & documented by carrier. It is
                ineligible for warranty claims unless reports of defects are
                submitted within 14 days of delivery. Cosmetic damage isn&apos;t
                covered.
              </div>
            </div>
          </div>
        )}
        {issue ? (
          <div className="text-sm font-bold text-red mt-4">
            {`${issue} Please remove this item from the cart to continue to checkout.`}
          </div>
        ) : (
          product.eligible_protection_plans?.length > 0 && (
            <ProtectionPlanButtonAndModal
              eligible_plans={product.eligible_protection_plans}
              forPage="cart"
              isRemovePPError={isRemovePPError}
              isRemovePPLoading={isRemovePPLoading}
              openModal={openProtectionModal}
              productId={product.id}
              removeProtectionPlan={setIsRemovePPLoading}
              selectedProtectionPlan={product.selected_protection_plan}
            />
          )
        )}
      </div>
    </div>
  );
};

CartProduct.propTypes = {
  cartToken: PropTypes.string,
  closeCart: PropTypes.func,
  openProtectionModal: PropTypes.func,
  hasImageLink: PropTypes.bool,
  product: PropTypes.shape({
    cart_item_errors: PropTypes.arrayOf(PropTypes.object),
    discount_amount: PropTypes.number,
    eligible_protection_plans: PropTypes.arrayOf(PropTypes.object),
    id: PropTypes.string,
    name: PropTypes.string,
    options: PropTypes.arrayOf(PropTypes.object),
    pickLater: PropTypes.bool,
    price_pre_discount: PropTypes.number,
    price: PropTypes.number,
    flash_sale: PropTypes.bool,
    is_clearance: PropTypes.bool,
    selected_protection_plan: PropTypes.object,
    stock_discount: PropTypes.number,
    product: PropTypes.shape({
      clearance_stock: PropTypes.object,
    }),
    sku: PropTypes.string,
  }),
  removeExtraCushion: PropTypes.func,
  removeItem: PropTypes.func,
  removeItemLoading: PropTypes.bool,
  viewType: PropTypes.string,
};

export default CartProduct;
